import React, { useRef, useLayoutEffect } from "react";
import { centsToCurrencyMask } from "../lib/currency";

export interface CurrencyInputProps {
  id?: string;
  name?: string;
  valueCents: number | null | undefined;
  onChangeCents: (cents: number | null) => void;
  allowEmpty?: boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  prefix?: string;
  className?: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  "aria-label"?: string;
}

/**
 * Campo de entrada monetária com máscara brasileira (X.XXX,XX):
 * - Inicia com centavos (0,00)
 * - Ao digitar qualquer número, os dígitos entram pela direita e deslocam para a esquerda
 * - Backspace remove da direita para a esquerda
 */
export function CurrencyInput({
  id,
  name,
  valueCents,
  onChangeCents,
  allowEmpty = false,
  placeholder = "0,00",
  required = false,
  disabled = false,
  prefix = "R$",
  className,
  style,
  autoFocus,
  "aria-label": ariaLabel,
}: CurrencyInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayValue = centsToCurrencyMask(valueCents, allowEmpty);

  // Mantém o cursor à direita após a reformatação para digitação natural
  useLayoutEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [displayValue]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, "");

    if (!digits) {
      onChangeCents(allowEmpty ? null : 0);
      return;
    }

    // Limita a 11 dígitos para evitar overflow numérico (até R$ 999.999.999,99)
    const truncatedDigits = digits.slice(0, 11);
    const cents = parseInt(truncatedDigits, 10);
    onChangeCents(cents);
  }

  function handleFocus() {
    if (inputRef.current) {
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }

  function handleClick() {
    if (inputRef.current) {
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Se o campo permite ficar vazio e já está em "0,00", Backspace limpa o campo
    if (allowEmpty && (e.key === "Backspace" || e.key === "Delete")) {
      const currentDigits = (inputRef.current?.value || "").replace(/\D/g, "");
      if (currentDigits === "" || currentDigits === "0" || currentDigits === "00") {
        e.preventDefault();
        onChangeCents(null);
      }
    }
  }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        width: "100%",
        maxWidth: 320,
        position: "relative",
      }}
    >
      {prefix && (
        <span
          style={{
            position: "absolute",
            left: "12px",
            color: "var(--color-text-muted, #64748b)",
            fontWeight: 600,
            fontSize: "var(--text-sm, 14px)",
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 1,
          }}
        >
          {prefix}
        </span>
      )}
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        placeholder={placeholder}
        className={className}
        style={{
          width: "100%",
          paddingLeft: prefix ? "38px" : "12px",
          paddingRight: "12px",
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          fontFeatureSettings: '"tnum"',
          fontWeight: 600,
          ...style,
        }}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onClick={handleClick}
      />
    </div>
  );
}
